import React from "react";
import { get, isFunction, isObject, map } from "lodash";
import { formatValue } from "utils/number";
import { Form } from "react-bootstrap";
import Table from "react-bootstrap/Table";

export interface IconTableColumn<T extends object> {
  label?: string;
  key?: string;
  format?: string | ((data: unknown) => unknown);
  customComponent?: React.ComponentType<T & Record<string, unknown>>;
  metadata?: Record<string, unknown>;
}

interface IconTableProps<T extends object> {
  columns: Array<IconTableColumn<T>>;
  data?: T[];
  hasSelectableRows?: boolean;
  onSelectAll?: React.ChangeEventHandler<HTMLInputElement>;
  onRowSelect?: (name: string, checked: boolean) => void;
  selectedRows?: Record<string, boolean>;
  hasSelectedAll?: boolean;
  uniqueRowIdentifier?: string;
  disable?: boolean;
}

const IconTable = <T extends object>({
  data,
  columns,
  hasSelectableRows,
  onSelectAll,
  onRowSelect,
  hasSelectedAll,
  selectedRows,
  uniqueRowIdentifier,
  disable,
}: IconTableProps<T>) => {
  return (
    <div className="icon-table">
      <Table responsive striped>
        <thead>
          <tr>
            {hasSelectableRows && (
              <th style={{ width: 25, textAlign: "center", borderTop: "none" }}>
                {isFunction(onSelectAll) && (
                  <Form.Check
                    id="selectAllRows"
                    type="checkbox"
                    name="selectAllRows"
                    checked={hasSelectedAll}
                    onChange={onSelectAll}
                  />
                )}
              </th>
            )}
            {columns.map((item, index) => {
              return <th key={index}>{item.label}</th>;
            })}
          </tr>
        </thead>
        <tbody>
          {map(data, (rowData, idx) => {
            const rowId = get(rowData, uniqueRowIdentifier as string) as string;
            return (
              <tr key={`table-row-${idx}`}>
                {hasSelectableRows && (
                  <th
                    key={`selectable-row-${idx}`}
                    style={{
                      width: 25,
                      textAlign: "center",
                      borderTop: "none",
                    }}
                  >
                    {!(rowData as { unselectable?: boolean }).unselectable && (
                      <Form.Check
                        id={rowId}
                        type="checkbox"
                        name={rowId}
                        checked={hasSelectedAll || get(selectedRows, rowId)}
                        onChange={(e) =>
                          onRowSelect?.(e.target.name, e.target.checked)
                        }
                        disabled={disable}
                      />
                    )}
                  </th>
                )}

                {columns.map((columnItem, index) => {
                  if (columnItem.customComponent) {
                    const CustomComponent = columnItem.customComponent;
                    const customComponentProps = {
                      ...rowData,
                      ...(isObject(columnItem.metadata)
                        ? columnItem.metadata
                        : {}),
                    };
                    return (
                      <td key={`custom-component-${index}`}>
                        <CustomComponent {...customComponentProps} />
                      </td>
                    );
                  }

                  return (
                    <>
                      <td key={`column-${index}`}>
                        {
                          formatValue(
                            columnItem,
                            get(rowData, columnItem.key as string)
                          ) as React.ReactNode
                        }
                      </td>
                    </>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default IconTable;
